"use client"

import { useCallback, useEffect, useRef, type ReactNode } from "react"
import Image from "next/image"

/**
 * Immersive scroll-pinned reveal — a full-bleed photo that blurs as you scroll
 * through it while an intro layer slides up out of view and a content layer
 * rises into its place.
 *
 * Mechanic: an outer full-bleed `track` (height `trackVh`) gives the scroll
 * runway; a child `stage` is `sticky top-0 h-screen` and stays pinned while you
 * scroll through it. A rAF-throttled scroll listener computes progress `p`, and
 * a single `applyProgress(p)` writes CSS custom properties on the stage
 * imperatively — no React state, no re-render per frame. The layers read those
 * vars with `var()`.
 *
 * Blur is a STATIC pre-blurred layer plus a sharp layer whose opacity
 * cross-fades over it; never animate `filter: blur` per frame. No
 * framer-motion. Honours `prefers-reduced-motion` by jumping to the final
 * state and attaching no listener.
 *
 * ⚠️ After editing `applyProgress` you MUST full-reload to test — HMR won't
 * re-attach the scroll listener.
 *
 * Generalised from the per-page copies on /go/immunity and /go/female-hormones
 * (`ImmersiveFullPicture` / `HormoneReveal`), which still carry their own
 * inline versions; migrate them opportunistically, not in an unrelated PR.
 */
export type ImmersiveRevealProps = {
  /** Full-bleed background photo (Blob URL). */
  image: string
  /** `object-position` for the background, e.g. `"62% 45%"`. */
  objectPosition?: string
  /** Shown over the sharp photo at rest; slides up and out as you scroll. */
  intro: ReactNode
  /** Rises from below and fades in as the photo blurs. */
  children: ReactNode
  /** Scroll runway as a vh multiple of the pinned stage. Default 240. */
  trackVh?: number
  /** Prioritise the background decode (only for an above-the-fold instance). */
  priority?: boolean
  /**
   * `stacked` (default) — intro slides up and out, content rises into its
   * place. Two beats, one column, long runway.
   *
   * `split` — intro holds on the left and content fades in beside it on the
   * right. One beat, so the content sits next to the argument it belongs to
   * rather than after it, and the runway can be much shorter. Below `lg` it
   * stacks into one column with both visible.
   */
  layout?: "stacked" | "split"
  /**
   * Override the legibility scrim. The default is weighted hard to the left for
   * a single left-hand column; a `split` instance needs a more even wash so
   * both columns read. Any CSS `background` value.
   */
  scrim?: string
}

/** Left-weighted — for a single content column over the sharp photo. */
const SCRIM_LEFT =
  "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.44) 42%, rgba(0,0,0,0.14) 74%), linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 42%)"

/* Scroll tuning — the immunity IMMERSIVE_DEFAULTS, kept identical so all three
   immersive sections feel the same under the thumb. */
const BLUR_START = 0.04 // photo blur begins (early)
const BLUR_END = 0.2 // …and finishes quickly
const REVEAL_START = 0.2 // content slide begins
const REVEAL_END = 0.9 // …reaches final position
const RISE_RATIO = 1 // 1:1 — content moves the reveal window's scroll px
/* `split` is a gentle fade-in beside the intro, not a full-height travel: the
   stacked form's 1:1 rise puts the column near the floor of the frame at rest,
   which reads as a layout bug rather than an entrance. */
const SPLIT_RISE_PX = 72
const FADE_BAND = 150 // px over which the content fades in, low in view
const MAX_BLUR = 6
const DARKEN = 0.08

export function ImmersiveReveal({
  image,
  objectPosition = "center",
  intro,
  children,
  trackVh = 240,
  priority = false,
  layout = "stacked",
  scrim = SCRIM_LEFT,
}: ImmersiveRevealProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const totalRef = useRef(1) // total scroll px (track.offsetHeight - innerHeight)

  // One imperative writer; sets CSS vars on the stage — NO React state per frame.
  const applyProgress = useCallback(
    (p: number) => {
      const stage = stageRef.current
      if (!stage) return
      const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
      const t = clamp((p - BLUR_START) / Math.max(0.01, BLUR_END - BLUR_START))
      const rl = clamp(
        (p - REVEAL_START) / Math.max(0.01, REVEAL_END - REVEAL_START)
      )
      const intervalPx = (REVEAL_END - REVEAL_START) * totalRef.current
      const travelPx = RISE_RATIO * intervalPx
      const offsetPx = (1 - rl) * travelPx // how far below final the block sits
      const x = clamp((window.innerHeight / 2 - offsetPx) / FADE_BAND)
      const split = layout === "split"
      // Split settles on the BLUR window (t), not the reveal window (rl): the
      // two columns are one composition, so the second must arrive as the photo
      // recedes — a few percent of scroll — not a fifth of the way down the
      // track. Holding it back to `rl` reproduces the detached-second-beat
      // problem the split layout exists to solve, just rotated 90°.
      const driver = split ? t : rl
      stage.style.setProperty("--sharp", `${1 - t}`)
      stage.style.setProperty("--reveal", `${driver}`) // LINEAR slide driver
      stage.style.setProperty("--op", `${split ? t : x * x * (3 - 2 * x)}`)
      stage.style.setProperty("--rise", `${split ? SPLIT_RISE_PX : travelPx}px`)
    },
    [layout]
  )

  const applyFromScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const total = track.offsetHeight - window.innerHeight
    totalRef.current = total
    const p =
      total > 0
        ? Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total))
        : 0
    applyProgress(p)
  }, [applyProgress])

  // scroll-driven blur/reveal (rAF-throttled). Reduced motion → final state, no listener.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const track = trackRef.current
      if (track) totalRef.current = track.offsetHeight - window.innerHeight
      applyProgress(1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
    }
    applyFromScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [applyFromScroll, applyProgress])

  return (
    <div
      ref={trackRef}
      className="relative w-screen"
      style={{
        height: `${trackVh}vh`,
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
      }}
    >
      <div
        ref={stageRef}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* background — static over-scale so blurred edges never show; a static
            pre-blurred base plus a sharp layer that cross-fades out. */}
        <div className="absolute inset-0" style={{ transform: "scale(1.05)" }}>
          <div
            className="absolute inset-0"
            style={{ filter: `blur(${MAX_BLUR}px) brightness(${1 - DARKEN})` }}
          >
            <Image
              src={image}
              alt=""
              fill
              sizes="100vw"
              className="object-cover"
              style={{ objectPosition }}
            />
          </div>
          <div
            className="absolute inset-0"
            style={{ opacity: "var(--sharp, 1)", willChange: "opacity" }}
          >
            <Image
              src={image}
              alt=""
              fill
              priority={priority}
              unoptimized
              sizes="100vw"
              className="object-cover"
              style={{ objectPosition }}
            />
          </div>
        </div>

        {/* legibility scrim */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: scrim }}
        />

        {layout === "split" ? (
          /* split — one layer, two columns. The intro holds; only the content
             column animates in, so the reader sees the claim and its support
             together instead of one replacing the other. */
          <div className="relative flex h-full items-center overflow-y-auto">
            <div className="grid w-full grid-cols-1 items-center gap-6 sm:gap-10 lg:grid-cols-2 lg:gap-16">
              <div>{intro}</div>
              {/* Defaults to VISIBLE (`--op, 1`), unlike the stacked form. In
                  split the content is half the section, so if the scroll
                  listener never runs — JS off, an error upstream — a column
                  stuck at opacity 0 is a blank half-frame rather than a missing
                  second beat. The listener sets the real value on mount. */}
              <div
                style={{
                  opacity: "var(--op, 1)",
                  transform:
                    "translateY(calc((1 - var(--reveal, 1)) * var(--rise, 0px)))",
                }}
              >
                {children}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-full">
            {/* intro layer — at rest over the sharp photo, slides up on scroll */}
            <div className="pointer-events-none absolute inset-0 flex items-center">
              <div
                className="w-full"
                style={{
                  transform: "translateY(calc(var(--reveal, 0) * -60vh))",
                }}
              >
                {intro}
              </div>
            </div>

            {/* content layer — rises from below and fades in as the photo blurs */}
            <div className="absolute inset-0 flex items-center">
              <div
                className="w-full"
                style={{
                  opacity: "var(--op, 0)",
                  transform:
                    "translateY(calc((1 - var(--reveal, 0)) * var(--rise, 72px)))",
                }}
              >
                {children}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
